
const root = document.documentElement;
const STORAGE_KEY = 'ry-theme-designer';
const PRESETS_KEY = 'ry-theme-designer-presets';
const PRESET_NAME_KEY = 'ry-theme-designer-preset-name';

// Knob elements by type
const colorKnobs = document.querySelectorAll('ry-color-input[data-knob]');
const sliderKnobs = document.querySelectorAll('ry-slider[data-knob]');
const selectKnobs = document.querySelectorAll('ry-select[data-knob]');
const groupKnobs = document.querySelectorAll('[data-knob]:has(ry-toggle-button)');

// ── Apply a knob value to :root ──

function applyKnob(prop, value, unit = '') {
  root.style.setProperty(prop, unit ? `${value}${unit}` : value);
}

// ── Read current state ──

function readState() {
  const state = {};
  colorKnobs.forEach(el => {
    state[el.dataset.knob] = el.getAttribute('value') || el.value;
  });
  sliderKnobs.forEach(el => {
    state[el.dataset.knob] = { value: el.getAttribute('value') || '0', unit: el.dataset.unit || '' };
  });
  selectKnobs.forEach(el => {
    if (el.id === 'preset-select') return;
    state[el.dataset.knob] = el.value;
  });
  groupKnobs.forEach(el => {
    const pressed = el.querySelector('ry-toggle-button[pressed]');
    state[el.dataset.knob] = pressed ? pressed.getAttribute('value') : '';
  });
  return state;
}

// ── Apply full state ──

function applyState(state) {
  for (const [prop, val] of Object.entries(state)) {
    if (typeof val === 'object' && val.value !== undefined) {
      // Slider
      const el = document.querySelector(`ry-slider[data-knob="${prop}"]`);
      if (el) el.setAttribute('value', val.value);
      applyKnob(prop, val.value, val.unit);
    } else {
      // Color, select, or button-group
      const colorEl = document.querySelector(`ry-color-input[data-knob="${prop}"]`);
      const selectEl = document.querySelector(`ry-select[data-knob="${prop}"]`);
      const groupEl = document.querySelector(`[data-knob="${prop}"]:has(ry-toggle-button)`);
      if (colorEl) { colorEl.setAttribute('value', val); applyKnob(prop, val); }
      if (selectEl) { selectEl.setAttribute('value', val); applyKnob(prop, val); }
      if (groupEl) {
        groupEl.querySelectorAll('ry-toggle-button').forEach(btn => {
          if (btn.getAttribute('value') === val) btn.setAttribute('pressed', '');
          else btn.removeAttribute('pressed');
        });
        applyKnob(prop, val);
      }
    }
  }
}

// ── Wire up color inputs ──

colorKnobs.forEach(el => {
  // Live update while dragging
  el.addEventListener('ry:input', (e) => {
    applyKnob(el.dataset.knob, e.detail.value);
    if (el.dataset.knob === '--ry-color-bg') syncColorScheme();
  });
  // Save on release
  el.addEventListener('ry:change', (e) => {
    const prop = el.dataset.knob;
    applyKnob(prop, e.detail.value);
    if (prop === '--ry-color-bg') syncColorScheme();
    // Store override if in alt mode
    if (inAltMode) {
      altOverrides[prop] = e.detail.value;
    }
    saveSession();
  });
});

// ── Wire up sliders ──

sliderKnobs.forEach(el => {
  const unit = el.dataset.unit || '';
  el.addEventListener('ry:input', (e) => {
    applyKnob(el.dataset.knob, e.detail.value, unit);
  });
  el.addEventListener('ry:change', (e) => {
    applyKnob(el.dataset.knob, e.detail.value, unit);
    saveSession();
  });
});

// ── Wire up selects ──

selectKnobs.forEach(el => {
  if (el.id === 'preset-select') return;
  el.addEventListener('ry:change', (e) => {
    applyKnob(el.dataset.knob, e.detail.value);
    saveSession();
  });
});

// ── Wire up button groups ──

groupKnobs.forEach(el => {
  el.addEventListener('ry:change', (e) => {
    applyKnob(el.dataset.knob, e.detail.value);
    saveSession();
  });
});

// ── Session persistence ──

function saveSession() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    state: readState(),
    altOverrides,
    inAltMode,
    basePalette,
  }));
}

function loadSession() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    try {
      const saved = JSON.parse(raw);
      // Support both old format (flat state) and new format (nested)
      if (saved.state) {
        applyState(saved.state);
        altOverrides = saved.altOverrides || {};
        inAltMode = saved.inAltMode || false;
        basePalette = saved.basePalette || null;
      } else {
        applyState(saved);
      }
    } catch {}
  }
  const presetName = localStorage.getItem(PRESET_NAME_KEY);
  if (presetName) {
    const sel = document.getElementById('preset-select');
    // ry-select needs to be upgraded and have its markup ready before .value works
    customElements.whenDefined('ry-select').then(() => {
      // Allow one microtask for setup() to complete
      requestAnimationFrame(() => { sel.value = presetName; });
    });
  }
}

// ── Presets ──

function loadPresets() {
  const raw = localStorage.getItem(PRESETS_KEY);
  return raw ? JSON.parse(raw) : {};
}

function savePreset(name) {
  const presets = loadPresets();
  presets[name] = readState();
  localStorage.setItem(PRESETS_KEY, JSON.stringify(presets));
}

document.getElementById('preset-select').addEventListener('ry:change', (e) => {
  const val = e.detail.value;
  if (!val) return;
  const [type, ...rest] = val.split(':');
  const name = rest.join(':');
  const preset = type === 'builtin' ? BUILTIN_PRESETS[name] : loadPresets()[name];
  if (preset) {
    applyState(preset);
    basePalette = null;
    altOverrides = {};
    inAltMode = false;
    syncColorScheme();
    saveSession();
    localStorage.setItem(PRESET_NAME_KEY, val);
  }
});

document.getElementById('save-btn').addEventListener('ry:click', () => {
  const name = prompt('Preset name:');
  if (name?.trim()) savePreset(name.trim());
});

document.getElementById('reset-btn').addEventListener('ry:click', () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(PRESET_NAME_KEY);
  basePalette = null;
  altOverrides = {};
  inAltMode = false;
  // Remove all inline styles from root
  const allKnobs = [...colorKnobs, ...sliderKnobs, ...selectKnobs, ...groupKnobs];
  allKnobs.forEach(el => {
    if (el.dataset.knob) root.style.removeProperty(el.dataset.knob);
  });
  // Reset component values to defaults
  colorKnobs.forEach(el => el.setAttribute('value', el.getAttribute('value') || ''));
  sliderKnobs.forEach(el => el.setAttribute('value', el.getAttribute('value') || '0'));
  location.reload();
});

document.getElementById('export-btn').addEventListener('ry:click', () => {
  const output = document.getElementById('export-output');
  output.classList.add('visible');

  // Build both palettes: base (current mode) and alt (derived + overrides)
  const basePalette = readColorPalette();
  const altPalette = {};
  for (const prop of Object.keys(basePalette)) {
    if (altOverrides[prop]) { altPalette[prop] = altOverrides[prop]; continue; }
    if (prop === '--ry-color-bg') { altPalette[prop] = basePalette['--ry-color-text']; continue; }
    if (prop === '--ry-color-text') { altPalette[prop] = basePalette['--ry-color-bg']; continue; }
    const [,, l] = hexToHsl(basePalette[prop]);
    altPalette[prop] = (l < 0.2 || l > 0.9) ? mirrorHex(basePalette[prop]) : basePalette[prop];
  }

  // Determine which is light, which is dark
  const baseIsLight = getLuminance(basePalette['--ry-color-bg'] || '#fff') >= 0.4;
  const lightPalette = baseIsLight ? basePalette : altPalette;
  const darkPalette = baseIsLight ? altPalette : basePalette;

  // Read non-color state
  const state = readState();

  let css = `[data-ry-theme="my-theme"] {\n`;
  for (const [prop, val] of Object.entries(state)) {
    if (prop.startsWith('--ry-color-')) {
      const lv = lightPalette[prop];
      const dv = darkPalette[prop];
      if (lv && dv && lv !== dv) {
        css += `  ${prop}: light-dark(${lv}, ${dv});\n`;
      } else {
        css += `  ${prop}: ${lv || dv || val};\n`;
      }
    } else {
      const value = typeof val === 'object' ? `${val.value}${val.unit}` : val;
      css += `  ${prop}: ${value};\n`;
    }
  }
  css += `}`;
  output.textContent = css;
  navigator.clipboard.writeText(css).then(() => {
    document.getElementById('export-btn').textContent = 'Copied!';
    setTimeout(() => { document.getElementById('export-btn').textContent = 'Export'; }, 1500);
  });
});

// ── Built-in presets ──

const BUILTIN_PRESETS = {
  'Midnight': {
    '--ry-color-primary': '#6366f1',
    '--ry-color-secondary': '#64748b',
    '--ry-color-accent': '#f472b6',
    '--ry-color-bg': '#0f172a',
    '--ry-color-text': '#e2e8f0',
    '--ry-color-success': '#22c55e',
    '--ry-color-warning': '#f59e0b',
    '--ry-color-danger': '#ef4444',
    '--ry-color-info': '#06b6d4',
    '--ry-radius-base': { value: '8', unit: 'px' },
    '--ry-shadow-strength': { value: '0.5', unit: '' },
    '--ry-shadow-spread': { value: '1.2', unit: '' },
    '--ry-space-scale': { value: '1', unit: '' },
    '--ry-duration-scale': { value: '1', unit: '' },
    '--ry-border-width': { value: '1', unit: 'px' },
    '--ry-outline-width': { value: '0', unit: 'px' },
    '--ry-panel-opacity': { value: '1', unit: '' },
    '--ry-panel-blur': { value: '0', unit: 'px' },
  },
  'Warm Earth': {
    '--ry-color-primary': '#b45309',
    '--ry-color-secondary': '#78716c',
    '--ry-color-accent': '#dc2626',
    '--ry-color-bg': '#fefce8',
    '--ry-color-text': '#422006',
    '--ry-color-success': '#65a30d',
    '--ry-color-warning': '#d97706',
    '--ry-color-danger': '#dc2626',
    '--ry-color-info': '#0891b2',
    '--ry-radius-base': { value: '3', unit: 'px' },
    '--ry-shadow-strength': { value: '1.5', unit: '' },
    '--ry-shadow-spread': { value: '0.8', unit: '' },
    '--ry-space-scale': { value: '1.1', unit: '' },
    '--ry-duration-scale': { value: '1.2', unit: '' },
    '--ry-border-width': { value: '1.5', unit: 'px' },
    '--ry-outline-width': { value: '0', unit: 'px' },
    '--ry-panel-opacity': { value: '1', unit: '' },
    '--ry-panel-blur': { value: '0', unit: 'px' },
  },
  'Glassmorphic': {
    '--ry-color-primary': '#3b82f6',
    '--ry-color-secondary': '#94a3b8',
    '--ry-color-accent': '#a78bfa',
    '--ry-color-bg': '#1e293b',
    '--ry-color-text': '#f1f5f9',
    '--ry-color-success': '#34d399',
    '--ry-color-warning': '#fbbf24',
    '--ry-color-danger': '#f87171',
    '--ry-color-info': '#22d3ee',
    '--ry-radius-base': { value: '12', unit: 'px' },
    '--ry-shadow-strength': { value: '2', unit: '' },
    '--ry-shadow-spread': { value: '1.5', unit: '' },
    '--ry-space-scale': { value: '1.15', unit: '' },
    '--ry-duration-scale': { value: '1.3', unit: '' },
    '--ry-border-width': { value: '1', unit: 'px' },
    '--ry-outline-width': { value: '0', unit: 'px' },
    '--ry-panel-opacity': { value: '0.7', unit: '' },
    '--ry-panel-blur': { value: '16', unit: 'px' },
  },
  'Brutalist': {
    '--ry-color-primary': '#000000',
    '--ry-color-secondary': '#555555',
    '--ry-color-accent': '#ff0000',
    '--ry-color-bg': '#ffffff',
    '--ry-color-text': '#000000',
    '--ry-color-success': '#008000',
    '--ry-color-warning': '#ff8c00',
    '--ry-color-danger': '#ff0000',
    '--ry-color-info': '#0000ff',
    '--ry-radius-base': { value: '0', unit: 'px' },
    '--ry-shadow-strength': { value: '0', unit: '' },
    '--ry-shadow-spread': { value: '0', unit: '' },
    '--ry-space-scale': { value: '1', unit: '' },
    '--ry-duration-scale': { value: '0', unit: '' },
    '--ry-border-width': { value: '3', unit: 'px' },
    '--ry-outline-width': { value: '2', unit: 'px' },
    '--ry-panel-opacity': { value: '1', unit: '' },
    '--ry-panel-blur': { value: '0', unit: 'px' },
  },
  'Candy': {
    '--ry-color-primary': '#e91e90',
    '--ry-color-secondary': '#ff6b9d',
    '--ry-color-accent': '#00bfff',
    '--ry-color-bg': '#fff0f5',
    '--ry-color-text': '#4a154b',
    '--ry-color-success': '#2ecc71',
    '--ry-color-warning': '#f1c40f',
    '--ry-color-danger': '#e74c3c',
    '--ry-color-info': '#00bfff',
    '--ry-radius-base': { value: '20', unit: 'px' },
    '--ry-shadow-strength': { value: '1.8', unit: '' },
    '--ry-shadow-spread': { value: '1.3', unit: '' },
    '--ry-space-scale': { value: '1.2', unit: '' },
    '--ry-duration-scale': { value: '1.5', unit: '' },
    '--ry-border-width': { value: '2', unit: 'px' },
    '--ry-outline-width': { value: '0', unit: 'px' },
    '--ry-panel-opacity': { value: '0.95', unit: '' },
    '--ry-panel-blur': { value: '8', unit: 'px' },
  },
};

// ── HSL color helpers ──

function hexToHsl(hex) {
  hex = hex.replace('#', '');
  if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
  let r = parseInt(hex.slice(0,2),16)/255;
  let g = parseInt(hex.slice(2,4),16)/255;
  let b = parseInt(hex.slice(4,6),16)/255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  let h = 0, s = 0, l = (max+min)/2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d/(2-max-min) : d/(max+min);
    if (max === r) h = ((g-b)/d + (g < b ? 6 : 0))/6;
    else if (max === g) h = ((b-r)/d + 2)/6;
    else h = ((r-g)/d + 4)/6;
  }
  return [h, s, l];
}

function hslToHex(h, s, l) {
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1/6) return p + (q-p)*6*t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q-p)*(2/3-t)*6;
      return p;
    };
    const q = l < 0.5 ? l*(1+s) : l+s-l*s;
    const p = 2*l - q;
    r = hue2rgb(p, q, h+1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h-1/3);
  }
  return '#' + [r,g,b].map(c => Math.round(c*255).toString(16).padStart(2,'0')).join('');
}

function mirrorHex(hex) {
  const [h, s, l] = hexToHsl(hex);
  return hslToHex(h, s, 1 - l);
}

// ── Light/dark detection ──

function getLuminance(hex) {
  hex = hex.replace('#', '');
  if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
  const r = parseInt(hex.slice(0,2),16)/255;
  const g = parseInt(hex.slice(2,4),16)/255;
  const b = parseInt(hex.slice(4,6),16)/255;
  const toLinear = (c) => c <= 0.03928 ? c/12.92 : ((c+0.055)/1.055)**2.4;
  return 0.2126*toLinear(r) + 0.7152*toLinear(g) + 0.0722*toLinear(b);
}

function syncColorScheme() {
  const bgInput = document.querySelector('ry-color-input[data-knob="--ry-color-bg"]');
  const hex = bgInput?.getAttribute('value') || '#ffffff';
  root.style.colorScheme = getLuminance(hex) < 0.4 ? 'dark' : 'light';
  updateFabIcon();
}

function updateFabIcon() {
  const fab = document.getElementById('mode-fab');
  fab.textContent = root.style.colorScheme === 'dark' ? '🌙' : '☀️';
}

// ── Two-palette mode toggle ──

let basePalette = null;   // colors in the mode the user started in
let altOverrides = {};     // only colors manually changed in the alt mode
let inAltMode = false;

function readColorPalette() {
  const p = {};
  colorKnobs.forEach(el => { p[el.dataset.knob] = el.getAttribute('value') || '#888888'; });
  return p;
}

function applyColorPalette(palette) {
  for (const [prop, hex] of Object.entries(palette)) {
    const el = document.querySelector(`ry-color-input[data-knob="${prop}"]`);
    if (el) el.setAttribute('value', hex);
    applyKnob(prop, hex);
  }
}

document.getElementById('mode-fab').addEventListener('click', () => {
  if (!inAltMode) {
    // Save base, derive alt (mirror + overrides)
    basePalette = readColorPalette();
    const derived = {};
    for (const [prop, hex] of Object.entries(basePalette)) {
      if (altOverrides[prop]) { derived[prop] = altOverrides[prop]; continue; }
      if (prop === '--ry-color-bg') { derived[prop] = basePalette['--ry-color-text']; continue; }
      if (prop === '--ry-color-text') { derived[prop] = basePalette['--ry-color-bg']; continue; }
      // Flip near-black/near-white colors (L < 0.1 or L > 0.9)
      const [,, l] = hexToHsl(hex);
      derived[prop] = (l < 0.2 || l > 0.9) ? mirrorHex(hex) : hex;
    }
    applyColorPalette(derived);
    inAltMode = true;
  } else {
    // Return to base
    if (basePalette) applyColorPalette(basePalette);
    inAltMode = false;
  }
  syncColorScheme();
  saveSession();
});

// ── Init ──
loadSession();
syncColorScheme();
